import { useEffect, useState } from "react";
import FilterableEcgTable from "../components/FilterableEcgTable/FilterableEcgTable";
import SplitContainer from "../components/Shared/SplitContainer/SplitContainer";
import UserEcgs from "../components/UserEcgs/UserEcgs";
import { Constants } from "../constants";
import {
  EcgsTableActionType,
  useEcgsTableContext,
} from "../context/EcgsTableContext";
import {
  PaneProps,
  SplitContainerContextProvider,
} from "../context/SplitContainerContext";
import useResponsive from "../hooks/useResponsive";
import { IBaseUser } from "../models/base-user.interface";
import ecgsService from "../services/ecgs.service";
import LocalStorageUtil from "../utils/localStorage";

const defaultEcgTableSize = 54.75;
const minEcgTableSize = 29.75;
const minEcgMetaSize = 38;

const EcgsPage = () => {
  const {
    state: { selectedEcg },
    actions: { loadEcgs },
    dispatch,
  } = useEcgsTableContext();

  const { isMobile, isTablet } = useResponsive();

  const [ecgsTableSize, setEcgsTableSize] = useState(
    isTablet ? (selectedEcg ? 0 : 100) : defaultEcgTableSize
  );

  useEffect(() => {
    // On App close, release locked ECG if needed
    window.addEventListener(
      "beforeunload",
      ecgsService.releaseReviewLockedEcg,
      false
    );

    return () => {
      window.removeEventListener(
        "beforeunload",
        ecgsService.releaseReviewLockedEcg
      );

      const reviewLockedEcgId = LocalStorageUtil.get(
        Constants.REVIEW_LOCKED_ECG_ID_KEY
      );
      // On Ecg page unload (e.g. navigation), unlock selected ECG if needed
      if (reviewLockedEcgId) {
        ecgsService.unlockEcg(reviewLockedEcgId);
        LocalStorageUtil.remove([Constants.REVIEW_LOCKED_ECG_ID_KEY]);
      }
    };
  }, []);

  useEffect(() => {
    setEcgsTableSize(isTablet ? (selectedEcg ? 0 : 100) : defaultEcgTableSize);
  }, [isTablet, selectedEcg]);

  const ecgUpdatedHandler = async (reselectEcg?: boolean) => {
    await loadEcgs(undefined, reselectEcg);
  };

  const ecgReviewLockHandler = async (ecgId: string | null) => {
    LocalStorageUtil.set(Constants.REVIEW_LOCKED_ECG_ID_KEY, ecgId);
    if (!ecgId) {
      // Review timeout ended, reload ecgs
      await loadEcgs(undefined, false);
    }
  };

  const backHandler = () => {
    /**
     * On mobile, back should also deselect selected ecg,
     * to allow selecting the same ECG, after pressing back.
     */
    if (isTablet) {
      dispatch({
        type: EcgsTableActionType.SET_SELECTED_ECG,
        payload: null,
      });
    }
  };

  const ecgPatient: IBaseUser | null = selectedEcg
    ? {
        id: selectedEcg?.patient!.id,
        email: selectedEcg?.patient!.email ?? null,
        firstName: selectedEcg?.patient!.firstName,
        lastName: selectedEcg?.patient!.lastName,
        dateOfBirth: selectedEcg?.patient!.dateOfBirth,
        gender: selectedEcg?.patient!.gender,
      }
    : null;

  const panes: PaneProps[] = [
    {
      initialSize: ecgsTableSize,
      minSize: isTablet ? 0 : minEcgTableSize,
      maxSize: ecgsTableSize,
      content: (
        <FilterableEcgTable itemsPerPage={Constants.TABLE_ITEMS_PER_PAGE} />
      ),
    },
    {
      initialSize: 100 - ecgsTableSize,
      minSize: 100 - ecgsTableSize,
      maxSize: isTablet ? 100 : 100 - minEcgTableSize,
      isCollapsible: true,
      showToggle: true,
      togglePosition: isTablet ? "none" : "left",
      content: (
        <UserEcgs
          user={ecgPatient}
          selectedEcg={selectedEcg}
          minGraphWidth={
            100 - (isTablet && !isMobile ? minEcgMetaSize : ecgsTableSize)
          }
          onEcgUpdate={ecgUpdatedHandler}
          onEcgReviewLock={ecgReviewLockHandler}
          onBack={backHandler}
        />
      ),
    },
  ];

  return (
    <SplitContainerContextProvider panes={panes}>
      <SplitContainer type="vertical" isResize={false} />
    </SplitContainerContextProvider>
  );
};

export default EcgsPage;
